import { Grid, useMediaQuery } from "@mui/material";
import { Create, Delete } from "@mui/icons-material";
import { useAccount } from "wagmi";

import { Breadcrumbs, PageHeader } from "@dzambalaorg/mui-page-layout";
import { DeleteDialog } from "@dzambalaorg/mui-dialog-delete";
import { useDialogDelete, useFetchQuery } from "@dzambalaorg/react-hooks";
import { IPaginationResult } from "@dzambalaorg/types-collection";
import { ListAction, ListActions, ListItem, StyledPagination } from "@framework/styled";
import type { ITemplate } from "@framework/types";
import { ModuleType, TemplateStatus, TokenType } from "@framework/types";
import {
  ServiceType,
  TemplateMintButton,
  TemplateSearchForm,
  useGlobalDialogFormCallbacks,
  WithCheckPermissionsListWrapper,
} from "@framework/shared";
import { List } from "@dzambalaorg/ui-kit";

import { collectionsTemplateValidationSchema } from "./form/validation";
import { AdminDialogKeys } from "../../../../../shared";
import { collectionsTemplatesEditFilter, prepareCollectionsTemplatesInitialValues } from "./schemas";

const COLLECTION_TEMPLATES_URL = "/collection/templates";

export const CollectionTemplate = () => {
  const isMd = useMediaQuery(theme => theme.breakpoints.down("md"));

  const { isLoading, take, skip, onChangePage, hasNextPage, loadMoreRef, allRows, totalCount, refetch } = useFetchQuery<
    IPaginationResult<ITemplate>
  >(COLLECTION_TEMPLATES_URL, { enableInfiniteScroll: isMd });

  const { openDialog } = useGlobalDialogFormCallbacks<ITemplate, ITemplate>();
  const { handleDelete } = useDialogDelete<ITemplate>();

  const { address: account = "" } = useAccount();

  const collectionsTemplateEdit = (template: ITemplate) => () => {
    openDialog(AdminDialogKeys.COLLECTIONS_TEMPLATE_EDIT, template, {
      refetch,
      initialValues: template,
      message: "dialogs.edit",
      testId: "CollectionsTemplateDialogForm",
      url: `${COLLECTION_TEMPLATES_URL}/${template.id}`,
      validationSchema: collectionsTemplateValidationSchema,
      filter: collectionsTemplatesEditFilter,
      prepareInitialValues: prepareCollectionsTemplatesInitialValues,
      componentProps: {
        mode: "edit",
      },
    });
  };

  return (
    <Grid>
      <Breadcrumbs path={["dashboard", "collection", "collection.template"]} />

      <PageHeader message="pages.collection.templates" />

      <TemplateSearchForm
        initialSearchValues={{
          templateStatus: [TemplateStatus.ACTIVE],
          contractIds: [],
        }}
        service={ServiceType.ADMIN}
        contractType={[TokenType.ERC721]}
        contractModule={[ModuleType.COLLECTION]}
      />

      <WithCheckPermissionsListWrapper isLoading={isLoading} count={allRows.length}>
        {allRows.map(template => (
          <ListItem key={template.id} wrap account={account} contract={template.contract}>
            <List.ItemText sx={{ width: 0.6 }}>{template.title}</List.ItemText>
            <List.ItemText sx={{ width: { xs: 0.6, md: 0.2 } }}>{template.contract?.title}</List.ItemText>
            <ListActions dataTestId="TemplateActionsMenuButton">
              <ListAction
                onClick={collectionsTemplateEdit(template)}
                message="form.buttons.edit"
                dataTestId="TemplateEditButton"
                icon={Create}
              />
              <ListAction
                onClick={handleDelete(template)}
                message="form.buttons.delete"
                dataTestId="TemplateDeleteButton"
                icon={Delete}
                disabled={template.templateStatus === TemplateStatus.INACTIVE}
              />
              <TemplateMintButton template={template} />
            </ListActions>
          </ListItem>
        ))}
      </WithCheckPermissionsListWrapper>

      {!isMd && totalCount > take && (
        <StyledPagination
          shape="rounded"
          page={skip / take + 1}
          count={Math.ceil(totalCount / take)}
          onChange={onChangePage}
        />
      )}
      {isMd && hasNextPage && <div ref={loadMoreRef} style={{ height: "20px", margin: "20px 0" }} />}

      <DeleteDialog url={COLLECTION_TEMPLATES_URL} refetch={refetch} />
    </Grid>
  );
};
